import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Card, Btn, Empty, Modal } from "../components/UI";
import { NativeLikeSelect } from "../components/CocoaSelect";
import { SlidePanel, ConfirmDialog, ReadOnlyChip } from "../components/SlidePanel";
import { FLabel, FSelect, FTextarea } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { Plus, Eye, Pencil, Trash2, Filter } from "lucide-react";
import { toast } from "sonner";

export const AIMS_ITEMS = [
  ["facial_expression", "Muscles of Facial Expression", "Fac. Expr."],
  ["lips", "Lips and Perioral Area", "Lips"],
  ["jaw", "Jaw", "Jaw"],
  ["tongue", "Tongue", "Tongue"],
  ["upper_extremities", "Upper Extremities (arms, wrists, hands, fingers)", "Upper Extr."],
  ["lower_extremities", "Lower Extremities (legs, knees, ankles, toes)", "Lower Extr."],
  ["trunk", "Trunk (neck, shoulders, hips)", "Trunk"],
  ["severity", "Severity of Abnormal Movements", "Severity"],
  ["incapacitation", "Incapacitation Due to Abnormal Movements", "Incapac."],
  ["awareness", "Patient's Awareness of Abnormal Movements", "Awareness"],
];
const SCORE_LABELS = ["0 - None", "1 - Minimal", "2 - Mild", "3 - Moderate", "4 - Severe"];
const emptyScores = () => Object.fromEntries(AIMS_ITEMS.map(([k]) => [k, "0"]));

export function AimsPanel({ client }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ date: dayjs().format("YYYY-MM-DD"), scores: emptyScores(), notes: "" });
  const [viewRec, setViewRec] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [minSeverity, setMinSeverity] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/clients/${client.id}/aims`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = minSeverity === "" ? items : items.filter((a) => a.scores.severity >= Number(minSeverity));

  const openAdd = () => { setForm({ date: dayjs().format("YYYY-MM-DD"), scores: emptyScores(), notes: "" }); setEditId(null); setModal(true); };
  const openEdit = (a) => {
    setForm({ date: a.date, scores: Object.fromEntries(AIMS_ITEMS.map(([k]) => [k, String(a.scores[k])])), notes: a.notes || "" });
    setEditId(a.id); setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, client_id: client.id };
      if (editId) await api.put(`/aims/${editId}`, body);
      else await api.post("/aims", body);
      toast.success(editId ? "AIMS record updated" : "AIMS record added");
      setModal(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  const doDelete = async () => {
    await api.delete(`/aims/${confirmDelete.id}`);
    toast.success("AIMS record deleted");
    setConfirmDelete(null); load();
  };

  return (
    <Card data-testid="aims-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 p-5 pb-3">
        <h3 className="font-heading font-bold text-[#3E2723]">All AIMS</h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-lg border border-[#E5DFD3] pl-3 pr-1 h-10">
            <Filter size={14} className="text-[#7B6157] shrink-0" />
            <NativeLikeSelect data-testid="aims-severity-filter" value={minSeverity} onChange={(e) => setMinSeverity(e.target.value)}
              triggerClass="h-8 w-36 border-0 bg-transparent px-1 text-sm text-[#4E342E] focus:ring-0">
              <option value="">Filter by: All</option>
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>Severity ≥ {n}</option>)}
            </NativeLikeSelect>
          </div>
          <Btn data-testid="add-aims-btn" variant="outline" className="rounded-lg" onClick={openAdd} disabled={!editable}><Plus size={14} /> Add New AIMS</Btn>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead><tr>{["Date", ...AIMS_ITEMS.map(([, , s]) => s), ""].map((h, i) => (
            <th key={i} className="bg-[#FDFCF8] border-y border-[#EFEBE0] text-[#4E342E] font-semibold py-3 px-3 whitespace-nowrap">{h}</th>))}</tr></thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} data-testid={`aims-row-${a.id}`} className="hover:bg-[#FDFCF8]">
                <td className="border-b border-[#EFEBE0] py-3 px-3 tabular-nums whitespace-nowrap">{dayjs(a.date).format("MM/DD/YYYY")}</td>
                {AIMS_ITEMS.map(([k]) => (
                  <td key={k} className={`border-b border-[#EFEBE0] py-3 px-3 tabular-nums ${a.scores[k] >= 3 ? "font-bold text-[#C62828]" : ""}`}>{a.scores[k]}</td>
                ))}
                <td className="border-b border-[#EFEBE0] py-3 px-3">
                  <div className="flex gap-1.5 justify-end">
                    <button type="button" data-testid={`view-aims-${a.id}`} aria-label="View" onClick={() => setViewRec(a)} className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Eye size={13} /></button>
                    {editable && <button type="button" data-testid={`edit-aims-${a.id}`} aria-label="Edit" onClick={() => openEdit(a)} className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>}
                    {editable && <button type="button" data-testid={`delete-aims-${a.id}`} aria-label="Delete" onClick={() => setConfirmDelete(a)} className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <Empty text="No AIMS records for this client" />}
      </div>

      <SlidePanel open={modal} onClose={() => setModal(false)} title={editId ? "Edit AIMS Exam" : "New AIMS Exam"}
        subtitle={`${client.first_name} ${client.last_name} - Abnormal Involuntary Movement Scale`} testId="aims-slide-panel">
        <form onSubmit={save} className="space-y-4">
          <div className="w-56"><FLabel>Exam Date</FLabel><DateField testId="aims-date-input" value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} noFuture /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
            {AIMS_ITEMS.map(([k, label]) => (
              <div key={k}>
                <FLabel>{label}</FLabel>
                <FSelect data-testid={`aims-score-${k}`} value={form.scores[k]} onChange={(e) => setForm((f) => ({ ...f, scores: { ...f.scores, [k]: e.target.value } }))}>
                  {SCORE_LABELS.map((s, i) => <option key={i} value={String(i)}>{s}</option>)}
                </FSelect>
              </div>
            ))}
          </div>
          <div><FLabel>Notes</FLabel><FTextarea data-testid="aims-notes-input" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setModal(false)}>Cancel</Btn>
            <Btn data-testid="aims-save-btn" type="submit" disabled={busy}>{editId ? "Save" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <Modal open={!!viewRec} onClose={() => setViewRec(null)} title="AIMS Exam" wide>
        {viewRec && (
          <div className="space-y-3" data-testid="aims-view-modal">
            <div className="text-sm text-[#7B6157]">{dayjs(viewRec.date).format("MM/DD/YYYY")} · Examiner: {viewRec.examiner || "-"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {AIMS_ITEMS.map(([k, label]) => (
                <div key={k} className="flex justify-between gap-3 text-sm border-b border-[#F1EEE7] py-1.5">
                  <span className="text-[#6D4C41]">{label}</span>
                  <span className={`font-bold ${viewRec.scores[k] >= 3 ? "text-[#C62828]" : "text-[#3E2723]"}`}>{viewRec.scores[k]}</span>
                </div>
              ))}
            </div>
            {viewRec.notes && <p className="text-sm text-[#6D4C41] bg-[#F5F2EB] rounded-xl p-3">{viewRec.notes}</p>}
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} onConfirm={doDelete}
        title="Delete AIMS record"
        description={confirmDelete ? `Delete the AIMS exam dated ${dayjs(confirmDelete.date).format("MM/DD/YYYY")} for ${client.first_name} ${client.last_name}?` : ""} />
    </Card>
  );
}
